import { ChangeDetectionStrategy, Component, DestroyRef, inject, Injector, signal } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { NgComponentOutlet } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon/icon';
import { BulkEditSectionFormService } from './bulk-edit-section-form.service';
import { BULK_UPDATE_QUEUE, type BulkEditPayload } from './bulk-edit.models';
import type { BulkEditPropertyDialogData, BulkEditPropertyDialogResult } from './bulk-edit-inline.types';
import type { BulkEditSection, BulkPatchFragment } from './bulk-edit-section.types';
import { BulkEditService } from './bulk-edit.service';

@Component({
  selector: 'zag-bulk-edit-property-dialog',
  templateUrl: './bulk-edit-property-dialog.component.html',
  styleUrl: './bulk-edit-property-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BulkEditSectionFormService],
  imports: [NgComponentOutlet, TranslatePipe, ZagButton, ZagIcon],
})
export class BulkEditPropertyDialogComponent {
  private static nextId = 0;

  protected readonly dialogRef = inject<DialogRef<BulkEditPropertyDialogResult>>(DialogRef);
  protected readonly data = inject<BulkEditPropertyDialogData>(DIALOG_DATA);
  protected readonly sectionInjector = inject(Injector);
  protected readonly titleId = `zag-bulk-edit-property-dialog-title-${BulkEditPropertyDialogComponent.nextId++}`;
  protected readonly descriptionId = `${this.titleId}-description`;
  protected readonly errorId = `${this.titleId}-error`;

  private readonly bulkEditService = inject(BulkEditService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formService = inject(BulkEditSectionFormService);

  protected readonly errorKey = signal<string | null>(null);
  protected readonly isStarting = signal(false);

  protected readonly supportsClear = this.data.section.supportsClear === true && !this.data.section.appliesToArrayField;

  protected continue(): void {
    this.submit('set');
  }

  protected clearValue(): void {
    if (!this.supportsClear) return;
    this.submit('clear');
  }

  protected close(): void {
    if (this.isStarting()) return;
    this.dialogRef.close();
  }

  private submit(mode: 'set' | 'clear'): void {
    if (this.isStarting()) return;

    const fragment = this.buildFragment(this.data.section, mode);
    if (!fragment) {
      this.errorKey.set('bulkEdit.inline.invalidValue');
      return;
    }

    const payload: Omit<BulkEditPayload, 'dryRun'> = {
      selection: this.data.selectionPayload,
      ...(fragment.patch ? { patch: fragment.patch } : {}),
      ...(fragment.arrayOps ? { arrayOps: fragment.arrayOps } : {}),
    };

    this.errorKey.set(null);
    this.isStarting.set(true);
    this.dialogRef.disableClose = true;
    this.bulkEditService
      .startApply(this.data.resourcePath, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ jobId, backgroundJobId }) => {
          const gatewayJobId = backgroundJobId ?? jobId;
          this.dialogRef.close({
            jobId,
            gatewayJobId,
            propertyLabelKey: this.data.section.titleKey,
            total: this.data.selectedCount,
            queueName: BULK_UPDATE_QUEUE,
          });
        },
        error: () => {
          this.isStarting.set(false);
          this.dialogRef.disableClose = false;
          this.errorKey.set('bulkEdit.inline.startError');
        },
      });
  }

  private buildFragment(section: BulkEditSection, mode: 'set' | 'clear'): BulkPatchFragment | null {
    try {
      const fragment = section.buildPatchFragment(this.formService.getState(section.id), mode);
      if (mode === 'set') return hasSetPayload(fragment) ? fragment : null;
      return buildClearFragment(fragment);
    } catch {
      return null;
    }
  }
}

function hasSetPayload(fragment: BulkPatchFragment): boolean {
  const hasSetPatch = Object.values(fragment.patch ?? {}).some(operation => operation.op === 'set' && operation.value !== undefined);
  const hasArrayOp = Object.values(fragment.arrayOps ?? {}).some(
    operation => (operation.op === 'append' || operation.op === 'remove') && operation.items.length > 0
  );
  return hasSetPatch || hasArrayOp;
}

function buildClearFragment(fragment: BulkPatchFragment): BulkPatchFragment | null {
  const fields = Object.keys(fragment.patch ?? {});
  if (fields.length === 0 || fragment.arrayOps) return null;
  return { patch: Object.fromEntries(fields.map(field => [field, { op: 'clear' as const }])) };
}
